import { useState, useRef, ClipboardEvent, MouseEvent, UIEvent } from "react";

import { rangeArray } from "common/utils/numbers";
import { getFirstElemIndexGreaterThanClientX } from "common/utils/dom";

import styles from "../../styles/VerificationInput.module.less";

type VerificationInputType = {
  size: number;
  onChange: (value: string) => void;
  value: string;
};

const resetSelectionRange = (input: HTMLInputElement) => {
  const end = input.value.length;
  input.setSelectionRange(end, end + 1);
};

const VerificationInput = (props: VerificationInputType) => {
  const { size, onChange, value } = props;

  // track focused state to display if active or not
  const [isFocused, setIsFocused] = useState(false);
  // track "clicking" state to get a slight animation guiding the user
  // to where the inputting of the value will starts
  const [isClicking, setIsClicking] = useState(false);

  const isFromMouseDownRef = useRef<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonsRef = useRef<DOMRect[]>([]);

  const activeIndex = value.length < size ? value.length : -1;

  /** Clean value before passing to onChange */
  const handleChange = (v: string) => {
    const cleaned = v.replace(/\D/g, "").slice(0, size);
    onChange(cleaned);
  };

  /**
   * Pass any clicks through nearest button,
   * rounding up because it will remove the content up to and including that button
   * */
  const handleClick = (e: MouseEvent<HTMLElement>) => {
    // we don't set focus until the click event to avoid repeated animations
    setIsFocused(true);
    const matchingIndex = getFirstElemIndexGreaterThanClientX(buttonsRef.current, e.clientX);
    if (matchingIndex === -1) {
      return;
    }
    setIsClicking(true);
    setTimeout(() => setIsClicking(false), 100);
    onChange(value.substr(0, matchingIndex));
  };

  /**
   * Unlike default paste behavior, replaces the entire content.
   */
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const data = e.clipboardData?.getData("text");
    if (!data) {
      return;
    }
    handleChange(data);
  };

  const handleMouseDown = () => {
    isFromMouseDownRef.current = true;
  };

  const handleFocus = () => {
    if (!isFromMouseDownRef.current) {
      setIsFocused(true);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    isFromMouseDownRef.current = false;
  };

  /** don't allow selecting multiple to clicks and drags leading to unexpected behavior */
  const handleSelect = (e: UIEvent<HTMLInputElement>) => {
    resetSelectionRange(e.target as HTMLInputElement);
  };

  return (
    <div className={styles.control}>
      {rangeArray(0, size).map((i) => (
        <button
          key={i}
          ref={(button) => {
            if (button) {
              buttonsRef.current.push(button.getBoundingClientRect());
            }
          }}
          type="button"
          tabIndex={-1}
          aria-hidden={true}
          className={`${styles.button} ${
            isFocused && (activeIndex === -1 || i === activeIndex) && !isClicking
              ? styles.active
              : ""
          }`}
        >
          <span className={styles.digit}>{value[i]}</span>
        </button>
      ))}
      <label className={styles.inputContainer}>
        <span className={styles.visuallyHidden}>{size} digit code</span>
        <input
          ref={inputRef}
          className={styles.input}
          inputMode="numeric"
          autoComplete="one-time-code"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onMouseDown={handleMouseDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          onPaste={handlePaste}
          onSelect={handleSelect}
        />
      </label>
    </div>
  );
};

export default VerificationInput;
